import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getElecricityEmissionsDataAPI,
  postWasteAndOtherActivitiesEnergyWttPreviewAPI,
} from 'src/utils/api';
import { toLocaleNumber } from 'src/utils/helpers';

type EnergyType = 'electricity' | 'heat' | 'steam';

const ENERGY_SOURCE_I18N_KEY: Record<EnergyType, string> = {
  electricity:
    'waste-and-other.other-energy-wtt.importFromScope2.source.electricity',
  heat: 'waste-and-other.other-energy-wtt.importFromScope2.source.heat',
  steam: 'waste-and-other.other-energy-wtt.importFromScope2.source.steam',
};

export type Scope3EnergyWttRow = {
  include_generation: boolean;
  comment?: string | null;
  emission_factor_is_known: false;
  energy_usage_data_factor_unknown: {
    spending_pln_or_kwh_per_period: string;
    unit: string;
  };
  energy_usage_data_factor_known: null;
  certified_sources_are_utilized: false;
  certified_energy_usage_data: null;
};

type PreviewRow = {
  id: string;
  include: boolean;
  source: EnergyType;
  amount: number;
  unit: string;
  include_generation: boolean;
  comment: string | null;
  emission_kg_eq_co2: number;
};

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value).trim().replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseUnitLabelToEnum(unitLabel: unknown): string | null {
  const s = String(unitLabel ?? '')
    .trim()
    .toLowerCase();
  if (!s) return null;
  if (s === 'kwh' || s === 'mwh' || s === 'gj') return s;
  if (s === 'heat_kwh' || s === 'heat_mwh' || s === 'heat_gj') return s;
  return null;
}

function normalizeHeatUnitToEnergyUnit(unit: string): string {
  const s = String(unit).trim();
  return s.startsWith('heat_') ? s.replace(/^heat_/, '') : s;
}

export default function ImportScope2EnergyToScope3WttModal({
  open,
  onClose,
  orgId,
  period,
  language,
  energyType,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  orgId: number;
  period: string;
  language: string;
  energyType: EnergyType;
  onConfirm: (_items: Scope3EnergyWttRow[]) => void;
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [rows, setRows] = useState<PreviewRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open) return;
      setLoading(true);
      setError(null);
      setRows([]);
      setSkippedCount(0);

      try {
        const scope2 = await getElecricityEmissionsDataAPI(orgId, period);
        const section = (scope2?.[`${energyType}_usage`] ?? []) as any[];

        const candidates: Omit<PreviewRow, 'emission_kg_eq_co2'>[] = [];
        let skipped = 0;
        let seq = 0;

        for (const entry of section) {
          const comment = entry?.comment ?? null;

          // Prefer factor-unknown (amount + enum unit)
          if (entry?.emission_factor_is_known === false) {
            const amount = toNumber(
              entry?.energy_usage_data_factor_unknown
                ?.spending_pln_or_kwh_per_period
            );
            const unit = entry?.energy_usage_data_factor_unknown?.unit;
            if (amount === null || !unit) {
              skipped += 1;
              continue;
            }

            const normalizedUnit = normalizeHeatUnitToEnergyUnit(unit);
            candidates.push({
              id: `${energyType}:unknown:${String(normalizedUnit)}:${String(
                amount
              )}:${String(comment ?? '')}:${seq++}`,
              include: true,
              source: energyType,
              amount,
              unit: normalizedUnit,
              include_generation: true,
              comment: comment ? String(comment) : null,
            });
            continue;
          }

          // Factor-known: use amount + unit_label (free solo) if parseable
          if (entry?.emission_factor_is_known === true) {
            const amount = toNumber(
              entry?.energy_usage_data_factor_known?.amount_per_period
            );
            const unitLabel = parseUnitLabelToEnum(
              entry?.energy_usage_data_factor_known?.unit_label
            );
            if (amount === null || !unitLabel) {
              skipped += 1;
              continue;
            }

            const normalizedUnit = normalizeHeatUnitToEnergyUnit(unitLabel);
            candidates.push({
              id: `${energyType}:known:${String(normalizedUnit)}:${String(
                amount
              )}:${String(comment ?? '')}:${seq++}`,
              include: true,
              source: energyType,
              amount,
              unit: normalizedUnit,
              include_generation: true,
              comment: comment ? String(comment) : null,
            });
            continue;
          }

          skipped += 1;
        }

        if (cancelled) return;
        setSkippedCount(skipped);

        if (candidates.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        const preview = await postWasteAndOtherActivitiesEnergyWttPreviewAPI(
          orgId,
          period,
          {
            energy_usage_type: energyType,
            items: candidates.map((c) => ({
              include_generation: c.include_generation,
              comment: c.comment,
              emission_factor_is_known: false,
              energy_usage_data_factor_unknown: {
                spending_pln_or_kwh_per_period: c.amount,
                unit: c.unit,
              },
              energy_usage_data_factor_known: null,
              certified_sources_are_utilized: false,
              certified_energy_usage_data: null,
            })),
          }
        );

        if (cancelled) return;
        const withEmissions: PreviewRow[] = candidates.map((c, idx) => ({
          ...c,
          emission_kg_eq_co2: preview[idx]?.emission_kg_eq_co2 ?? 0,
        }));
        setRows(withEmissions);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, period, energyType]);

  const selected = useMemo(() => rows.filter((r) => r.include), [rows]);
  const totalEmission = useMemo(
    () => selected.reduce((acc, r) => acc + (r.emission_kg_eq_co2 ?? 0), 0),
    [selected]
  );

  const setAll = (value: boolean) => {
    setRows((prev) => prev.map((r) => ({ ...r, include: value })));
  };

  const toggleRow = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, include: !r.include } : r))
    );
  };

  const handleConfirm = () => {
    const items: Scope3EnergyWttRow[] = selected.map((r) => ({
      include_generation: r.include_generation,
      comment: r.comment,
      emission_factor_is_known: false,
      energy_usage_data_factor_unknown: {
        spending_pln_or_kwh_per_period: String(r.amount),
        unit: r.unit,
      },
      energy_usage_data_factor_known: null,
      certified_sources_are_utilized: false,
      certified_energy_usage_data: null,
    }));
    onConfirm(items);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('waste-and-other.other-energy-wtt.importFromScope2.title')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          {t('waste-and-other.other-energy-wtt.importFromScope2.description')}
        </Typography>

        {skippedCount > 0 && (
          <Typography sx={{ mb: 2 }} color="text.secondary">
            {t('waste-and-other.other-energy-wtt.importFromScope2.skipped', {
              count: skippedCount,
            })}
          </Typography>
        )}

        {loading && (
          <Typography>
            {t('waste-and-other.other-energy-wtt.importFromScope2.loading')}
          </Typography>
        )}
        {!!error && (
          <Typography color="error" sx={{ whiteSpace: 'pre-wrap' }}>
            {t('waste-and-other.other-energy-wtt.importFromScope2.error')}:{' '}
            {error}
          </Typography>
        )}

        {!loading && !error && rows.length === 0 && (
          <Typography color="text.secondary">
            {t('waste-and-other.other-energy-wtt.importFromScope2.empty')}
          </Typography>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.length === rows.length}
                    indeterminate={
                      selected.length > 0 && selected.length < rows.length
                    }
                    onChange={(e) => setAll(e.target.checked)}
                  />
                }
                label={t(
                  'waste-and-other.other-energy-wtt.importFromScope2.selectAll'
                )}
              />
              <Typography sx={{ ml: 'auto' }}>
                {t('waste-and-other.other-energy-wtt.importFromScope2.total', {
                  value: toLocaleNumber(totalEmission, language),
                })}
              </Typography>
            </div>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    {t(
                      'waste-and-other.other-energy-wtt.importFromScope2.table.source'
                    )}
                  </TableCell>
                  <TableCell>
                    {t('waste-and-other.other-energy.table.power-spending')}
                  </TableCell>
                  <TableCell>
                    {t('waste-and-other.other-energy.table.unit')}
                  </TableCell>
                  <TableCell align="right">
                    {t(
                      'waste-and-other.other-energy-wtt.importFromScope2.table.emissions'
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={r.include}
                        onChange={() => toggleRow(r.id)}
                      />
                    </TableCell>
                    <TableCell>{t(ENERGY_SOURCE_I18N_KEY[r.source])}</TableCell>
                    <TableCell>{toLocaleNumber(r.amount, language)}</TableCell>
                    <TableCell>{t(`dropdowns.${r.unit}`)}</TableCell>
                    <TableCell align="right">
                      {toLocaleNumber(r.emission_kg_eq_co2, language)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !!error || selected.length === 0}
        >
          {t('waste-and-other.other-energy-wtt.importFromScope2.addSelected')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
